import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Map as MapIcon, Layers, Lock, Eye, EyeOff, Info, AlertCircle } from 'lucide-react';
import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  gisService,
  type GisLayer,
  type GeoJsonFeatureCollection,
} from '@/services/gisService';

export interface MapLayerView extends GisLayer {
  geometryType?: 'Point' | 'LineString' | 'Polygon' | 'MultiPolygon' | string;
  bbox?: [number, number, number, number];
  accessLevel?: string;
}

interface MapViewerProps {
  layers: MapLayerView[];
  restrictedLayers?: MapLayerView[];
  visibility: Record<string, boolean>;
  onToggle: (id: string) => void;
  /**
   * Injects the feature source. Defaults to the authenticated analyst loader
   * (gisService.getLayerFeatures). The anonymous Public GIS passes a public-safe,
   * uncredentialed loader that returns geometry only (no feature properties).
   */
  featureLoader?: (layerId: string) => Promise<{
    data: GeoJsonFeatureCollection | null;
    error: string | null;
  }>;
}

type LayerLoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; featureCount: number }
  | { kind: 'error'; message: string };

const DEFAULT_CENTER: [number, number] = [-1.0232, 7.9465];
const DEFAULT_ZOOM = 5.5;

const env =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

// R11P GEOLOGICAL P0.5-P9C — SOVEREIGN MAP POSTURE.
// The governed pilot path makes NO request to any third-party tile/basemap
// service (no OpenStreetMap, Google, Mapbox, MapTiler, Stadia, Carto, Esri,
// OpenFreeMap or external CDN). The default style is an empty, local,
// overlays-only style: GIISDEC vector/GeoJSON overlays render on a neutral
// background with full map controls. A future GIISDEC/NITA-hosted sovereign
// basemap MAY be supplied via VITE_MAP_STYLE_URL (unset by default) — it must
// never point at an uncontrolled public tile service.
const configuredStyleUrl = (env.VITE_MAP_STYLE_URL ?? '').trim();

const EMPTY_LOCAL_STYLE = {
  version: 8 as const,
  sources: {},
  layers: [],
};

const selectedStyle = configuredStyleUrl || EMPTY_LOCAL_STYLE;

function sourceId(layerId: string): string {
  return `giisdec-${layerId}`;
}

function removeMapLayer(map: MapLibreMap, layerId: string): void {
  const source = sourceId(layerId);

  for (const id of [`${source}-circle`, `${source}-line`, `${source}-fill`]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }

  if (map.getSource(source)) map.removeSource(source);
}

function addGeoJsonLayer(
  map: MapLibreMap,
  layerId: string,
  data: GeoJsonFeatureCollection,
): void {
  const source = sourceId(layerId);

  removeMapLayer(map, layerId);

  map.addSource(source, {
    type: 'geojson',
    data: data as GeoJSON.FeatureCollection,
  });

  map.addLayer({
    id: `${source}-fill`,
    type: 'fill',
    source,
    filter: [
      'match',
      ['geometry-type'],
      ['Polygon', 'MultiPolygon'],
      true,
      false,
    ],
    paint: {
      'fill-opacity': 0.25,
    },
  });

  map.addLayer({
    id: `${source}-line`,
    type: 'line',
    source,
    filter: [
      'match',
      ['geometry-type'],
      ['LineString', 'MultiLineString', 'Polygon', 'MultiPolygon'],
      true,
      false,
    ],
    paint: {
      'line-width': 2,
    },
  });

  map.addLayer({
    id: `${source}-circle`,
    type: 'circle',
    source,
    filter: [
      'match',
      ['geometry-type'],
      ['Point', 'MultiPoint'],
      true,
      false,
    ],
    paint: {
      'circle-radius': 5,
    },
  });
}

const MapViewer: React.FC<MapViewerProps> = ({
  layers,
  restrictedLayers = [],
  visibility,
  onToggle,
  featureLoader = gisService.getLayerFeatures,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const requestGenerationRef = useRef(0);
  // Per-layer token of the request whose result the map currently holds (or awaits). A layer is
  // fetched only when it has no token; a response is applied only if its token is still current.
  const layerRequestRef = useRef<Map<string, number>>(new Map());
  const loaderRef = useRef(featureLoader);

  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [activeInfo, setActiveInfo] = useState<string | null>(null);
  const [layerStates, setLayerStates] = useState<Record<string, LayerLoadState>>({});

  const visibleLayers = useMemo(
    () => layers.filter((layer) => visibility[layer.id] ?? true),
    [layers, visibility],
  );

  const renderedLayerCount = useMemo(
    () =>
      visibleLayers.filter((layer) => {
        const state = layerStates[layer.id];
        return state?.kind === 'ready' && state.featureCount > 0;
      }).length,
    [visibleLayers, layerStates],
  );

  const totalFeatureCount = useMemo(
    () =>
      visibleLayers.reduce((total, layer) => {
        const state = layerStates[layer.id];
        return total + (state?.kind === 'ready' ? state.featureCount : 0);
      }, 0),
    [visibleLayers, layerStates],
  );

  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;
    const layerRequests = layerRequestRef.current;

    try {
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: selectedStyle as maplibregl.StyleSpecification,
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
      });

      map.addControl(
        new maplibregl.NavigationControl({ showCompass: false }),
        'top-right',
      );
      map.addControl(
        new maplibregl.ScaleControl({ unit: 'metric' }),
        'bottom-left',
      );

      mapRef.current = map;

      map.once('load', () => {
        if (!cancelled) setMapReady(true);
      });

      map.on('error', (event) => {
        if (!cancelled && event.error) {
          setMapError(event.error.message || 'Map failed to load.');
        }
      });
    } catch (error) {
      if (!cancelled) {
        setMapError(
          error instanceof Error ? error.message : 'Map failed to initialize.',
        );
      }
    }

    return () => {
      cancelled = true;
      requestGenerationRef.current += 1;
      layerRequests.clear();

      try {
        mapRef.current?.remove();
      } catch {
        /* no-op */
      }

      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const requests = layerRequestRef.current;
    const visibleIds = new Set(visibleLayers.map((layer) => layer.id));

    // A different loader is a different data context (e.g. another account): every visible layer
    // is requested again and anything issued under the previous loader is discarded.
    if (loaderRef.current !== featureLoader) {
      loaderRef.current = featureLoader;
      requests.clear();
    }

    for (const layerId of [...requests.keys()]) {
      if (!visibleIds.has(layerId)) {
        requests.delete(layerId);
        removeMapLayer(map, layerId);
      }
    }

    for (const layer of layers) {
      if (!visibleIds.has(layer.id)) {
        removeMapLayer(map, layer.id);
      }
    }

    // Only layers without a current request are fetched; toggling one layer never refetches others.
    for (const layer of visibleLayers) {
      if (requests.has(layer.id)) continue;

      const token = ++requestGenerationRef.current;
      requests.set(layer.id, token);

      setLayerStates((current) => ({
        ...current,
        [layer.id]: { kind: 'loading' },
      }));

      void featureLoader(layer.id).then((result) => {
        if (layerRequestRef.current.get(layer.id) !== token || !mapRef.current) return;

        if (result.error || !result.data) {
          removeMapLayer(mapRef.current, layer.id);
          setLayerStates((current) => ({
            ...current,
            [layer.id]: {
              kind: 'error',
              message: result.error || 'Layer geometry is unavailable.',
            },
          }));
          return;
        }

        const featureCount = Array.isArray(result.data.features)
          ? result.data.features.length
          : 0;

        if (featureCount === 0) {
          removeMapLayer(mapRef.current, layer.id);
          setLayerStates((current) => ({
            ...current,
            [layer.id]: { kind: 'ready', featureCount: 0 },
          }));
          return;
        }

        try {
          addGeoJsonLayer(mapRef.current, layer.id, result.data);
          setLayerStates((current) => ({
            ...current,
            [layer.id]: { kind: 'ready', featureCount },
          }));
        } catch (error) {
          removeMapLayer(mapRef.current, layer.id);
          setLayerStates((current) => ({
            ...current,
            [layer.id]: {
              kind: 'error',
              message:
                error instanceof Error
                  ? error.message
                  : 'Layer geometry could not be rendered.',
            },
          }));
        }
      });
    }
  }, [layers, visibleLayers, mapReady, featureLoader]);

  const visibleLoading = visibleLayers.some(
    (layer) => layerStates[layer.id]?.kind === 'loading',
  );

  const visibleErrors = visibleLayers.filter(
    (layer) => layerStates[layer.id]?.kind === 'error',
  );

  const visibleReady = visibleLayers.filter(
    (layer) => layerStates[layer.id]?.kind === 'ready',
  );

  const zeroData =
    mapReady &&
    visibleLayers.length > 0 &&
    !visibleLoading &&
    visibleErrors.length === 0 &&
    visibleReady.length === visibleLayers.length &&
    totalFeatureCount === 0;

  return (
    <div className="bg-eds-surface border border-eds-border rounded-eds-lg overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-eds-border bg-eds-surface-2">
        <div>
          <h3 className="font-eds-heading font-semibold text-eds-fg flex items-center gap-2">
            <MapIcon className="w-4 h-4 text-eds-accent" /> GIS Map Viewer
          </h3>
          {!configuredStyleUrl && (
            <p className="font-eds-body text-[11px] text-eds-muted-fg mt-0.5">
              {'Sovereign basemap not configured · GIISDEC operational overlays only'}
            </p>
          )}
        </div>

        <span className="font-eds-mono text-xs text-eds-muted-fg">
          {renderedLayerCount} / {visibleLayers.length} layers rendered
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4">
        <aside className="lg:col-span-1 border-b lg:border-b-0 lg:border-r border-eds-border p-4 space-y-4">
          <div>
            <h4 className="font-eds-body text-xs font-semibold uppercase tracking-wide text-eds-muted-fg flex items-center gap-1.5 mb-2">
              <Layers className="w-3.5 h-3.5" /> Permitted Layers
            </h4>

            <ul className="space-y-1.5">
              {layers.map((layer) => {
                const on = visibility[layer.id] ?? true;
                const state = layerStates[layer.id];

                return (
                  <li key={layer.id}>
                    <div className="flex items-center justify-between gap-2 rounded-eds-sm px-2 py-1.5 hover:bg-eds-surface-2">
                      <button
                        type="button"
                        onClick={() => onToggle(layer.id)}
                        className="flex items-center gap-2 font-eds-body text-sm text-eds-fg min-w-0"
                        aria-pressed={on}
                      >
                        {on ? (
                          <Eye className="w-4 h-4 text-eds-accent shrink-0" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-eds-muted-fg shrink-0" />
                        )}
                        <span className="truncate">{layer.name}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setActiveInfo(activeInfo === layer.id ? null : layer.id)
                        }
                        className="text-eds-muted-fg hover:text-eds-fg shrink-0"
                        aria-label={`About ${layer.name}`}
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {activeInfo === layer.id && (
                      <div className="ml-6 mt-1 font-eds-body text-[11px] text-eds-muted-fg space-y-0.5">
                        <div>Geometry: {layer.geometryType ?? 'GeoJSON'}</div>
                        <div>
                          Access:{' '}
                          {layer.accessLevel ??
                            layer.classification ??
                            'permitted'}
                        </div>
                        <div>
                          Status:{' '}
                          {!on
                            ? 'Hidden'
                            : state?.kind === 'loading'
                              ? 'Loading'
                              : state?.kind === 'error'
                                ? 'Unavailable'
                                : state?.kind === 'ready'
                                  ? state.featureCount > 0
                                    ? `${state.featureCount} feature(s)`
                                    : 'No published features'
                                  : 'Ready to load'}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}

              {layers.length === 0 && (
                <li className="font-eds-body text-xs text-eds-muted-fg">
                  No permitted layers available.
                </li>
              )}
            </ul>
          </div>

          {restrictedLayers.length > 0 && (
            <div>
              <h4 className="font-eds-body text-xs font-semibold uppercase tracking-wide text-eds-muted-fg flex items-center gap-1.5 mb-2">
                <Lock className="w-3.5 h-3.5" /> Restricted
              </h4>

              <ul className="space-y-1.5">
                {restrictedLayers.map((layer) => (
                  <li
                    key={layer.id}
                    className="flex items-center justify-between gap-2 rounded-eds-sm px-2 py-1.5 bg-eds-surface-2 text-eds-muted-fg"
                    title="Access granted by GIISDEC only"
                  >
                    <span className="flex items-center gap-2 text-sm min-w-0">
                      <Lock className="w-4 h-4 shrink-0" />
                      <span className="truncate">{layer.name}</span>
                    </span>
                    <span className="text-[10px] uppercase font-semibold tracking-wide shrink-0">
                      Locked
                    </span>
                  </li>
                ))}
              </ul>

              <p className="mt-2 font-eds-body text-[11px] text-eds-muted-fg">
                Locked layers unlock only when GIISDEC grants access.
              </p>
            </div>
          )}
        </aside>

        <div className="lg:col-span-3 relative">
          <div
            className="relative w-full"
            style={{ aspectRatio: '16 / 9', minHeight: 320 }}
          >
            <div
              ref={containerRef}
              className="absolute inset-0 bg-eds-surface-2"
              aria-label="GIS map canvas"
            />

            {!mapReady && !mapError && (
              <div className="absolute inset-0 flex items-center justify-center bg-eds-surface-2/80">
                <p className="font-eds-body text-sm text-eds-muted-fg">
                  Initializing GIS map…
                </p>
              </div>
            )}

            {mapError && (
              <div className="absolute inset-0 flex items-center justify-center p-6 bg-eds-surface/95">
                <div className="max-w-md text-center">
                  <AlertCircle className="w-7 h-7 text-eds-warning mx-auto mb-2" />
                  <h4 className="font-eds-heading font-semibold text-eds-fg">
                    Map unavailable
                  </h4>
                  <p className="font-eds-body text-sm text-eds-muted-fg mt-1.5">
                    {mapError}
                  </p>
                </div>
              </div>
            )}

            {mapReady && visibleLayers.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
                <p className="font-eds-body text-sm text-eds-muted-fg text-center bg-eds-surface/90 border border-eds-border rounded-eds-md px-4 py-3">
                  Enable a permitted layer to begin.
                </p>
              </div>
            )}

            {zeroData && (
              <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
                <div className="max-w-md text-center bg-eds-surface/95 border border-eds-border rounded-eds-lg px-6 py-5">
                  <Info className="w-7 h-7 text-eds-warning mx-auto mb-2" />
                  <h4 className="font-eds-heading font-semibold text-eds-fg">
                    No published geometry is currently available.
                  </h4>
                  <p className="font-eds-body text-sm text-eds-muted-fg mt-1.5">
                    Approved GIISDEC spatial features will appear here when
                    authoritative GIS data is published for your access level.
                  </p>
                </div>
              </div>
            )}

            {mapReady && visibleErrors.length > 0 && !visibleLoading && (
              <div className="absolute left-3 bottom-3 max-w-sm bg-eds-surface/95 border border-eds-border rounded-eds-md px-3 py-2 shadow-sm">
                <p className="font-eds-body text-xs text-eds-warning">
                  {visibleErrors.length} visible layer
                  {visibleErrors.length === 1 ? '' : 's'} could not be loaded.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between px-4 py-2 border-t border-eds-border bg-eds-surface-2 font-eds-body text-[11px] text-eds-muted-fg">
            <span>Coordinate reference system: EPSG:4326</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapViewer;
